import { CheckCircle, ReverseLeft } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/buttons/button";
import { cx } from "@/utils/cx";

import type { SensorAutoSaveStatus as AutoSaveStatus } from "../../../utils/sensor/sensor-auto-save";

import { getAutoSaveDisplay } from "../../../utils/shared/auto-save-display";

const AUTO_SAVE_STATUS_FADE_MS = 300;

interface AutoSaveStatusDisplayProps {
	areChangesSaved: boolean;
	className?: string;
	isSaving: boolean;
	label?: string;
	onUndo: () => void;
}

const AutoSaveStatusIcon = ({
	areChangesSaved,
	isSaving,
}: {
	areChangesSaved: boolean;
	isSaving: boolean;
}) => {
	if (isSaving) {
		return (
			<svg
				aria-hidden="true"
				className="size-3 shrink-0 animate-spin"
				fill="none"
				viewBox="0 0 24 24"
			>
				<circle
					className="opacity-25"
					cx="12"
					cy="12"
					r="10"
					stroke="currentColor"
					strokeWidth="3"
				/>
				<path
					className="opacity-75"
					d="M12 2a10 10 0 0 1 10 10"
					stroke="currentColor"
					strokeLinecap="round"
					strokeWidth="3"
				/>
			</svg>
		);
	}

	if (areChangesSaved) {
		return <CheckCircle className="size-3 shrink-0" />;
	}

	return null;
};

/**
 * Fades in when a save starts and fades out when the status clears after edits.
 * Text can update (Saving… → Saved) without re-triggering the enter animation.
 */
export const AutoSaveStatusDisplay = ({
	areChangesSaved,
	className,
	isSaving,
	label,
	onUndo,
}: AutoSaveStatusDisplayProps) => {
	const [displayLabel, setDisplayLabel] = useState<string | undefined>();
	const [visible, setVisible] = useState(false);
	const isShowingRef = useRef(false);

	useEffect(() => {
		let cancelled = false;

		if (!label) {
			if (!isShowingRef.current) return undefined;

			isShowingRef.current = false;
			const fadeOutFrame = requestAnimationFrame(() => {
				if (!cancelled) setVisible(false);
			});
			const unmountTimeout = window.setTimeout(() => {
				if (!cancelled) setDisplayLabel(undefined);
			}, AUTO_SAVE_STATUS_FADE_MS);

			return () => {
				cancelled = true;
				cancelAnimationFrame(fadeOutFrame);
				clearTimeout(unmountTimeout);
			};
		}

		const updateFrame = requestAnimationFrame(() => {
			if (cancelled) return;

			setDisplayLabel(label);

			if (isShowingRef.current) return;

			isShowingRef.current = true;
			requestAnimationFrame(() => {
				if (cancelled) return;
				setVisible(false);
				requestAnimationFrame(() => {
					if (!cancelled) setVisible(true);
				});
			});
		});

		return () => {
			cancelled = true;
			cancelAnimationFrame(updateFrame);
		};
	}, [label]);

	if (!displayLabel) return null;

	return (
		<div
			className={cx(
				"flex shrink-0 flex-row items-center gap-1 transition-opacity duration-300 ease-in-out",
				className,
				visible ? "opacity-100" : "opacity-0",
			)}
		>
			<p
				aria-live="polite"
				className={cx(
					"flex items-center gap-1 text-xs",
					areChangesSaved ? "mr-2 text-success-primary" : "text-tertiary",
				)}
				role="status"
			>
				<AutoSaveStatusIcon
					areChangesSaved={areChangesSaved}
					isSaving={isSaving}
				/>
				{displayLabel}
			</p>
			{areChangesSaved ? (
				<Button
					color="link-gray"
					iconTrailing={ReverseLeft}
					onClick={onUndo}
					size="sm"
				>
					Undo
				</Button>
			) : null}
		</div>
	);
};

interface SensorAutoSaveStatusProps {
	autoSaveStatus: AutoSaveStatus;
	canUndoSensorEdit: boolean;
	isEditDirty: boolean;
	isUpdatedSensorPending: boolean;
	onUndo: () => void;
}

interface ZoneAutoSaveStatusProps {
	autoSaveStatus: AutoSaveStatus;
	canUndoZoneEdit: boolean;
	isEditDirty: boolean;
	isUpdatedZonePending: boolean;
	onUndo: () => void;
}

/** Auto-save status for sensor edit, shown in the edit panel header. */
export const SensorAutoSaveStatus = ({
	autoSaveStatus,
	canUndoSensorEdit,
	isEditDirty,
	isUpdatedSensorPending,
	onUndo,
}: SensorAutoSaveStatusProps) => {
	const display = getAutoSaveDisplay({
		autoSaveStatus,
		canUndoEdit: canUndoSensorEdit,
		isEditDirty,
		isUpdatePending: isUpdatedSensorPending,
	});

	if (!display.isVisible) return null;

	return (
		<AutoSaveStatusDisplay
			areChangesSaved={display.areChangesSaved}
			isSaving={display.isSaving}
			label={display.label}
			onUndo={onUndo}
		/>
	);
};

/** Auto-save status for zone edit, shown in the edit panel header. */
export const ZoneAutoSaveStatus = ({
	autoSaveStatus,
	canUndoZoneEdit,
	isEditDirty,
	isUpdatedZonePending,
	onUndo,
}: ZoneAutoSaveStatusProps) => {
	const display = getAutoSaveDisplay({
		autoSaveStatus,
		canUndoEdit: canUndoZoneEdit,
		isEditDirty,
		isUpdatePending: isUpdatedZonePending,
	});

	if (!display.isVisible) return null;

	return (
		<AutoSaveStatusDisplay
			areChangesSaved={display.areChangesSaved}
			isSaving={display.isSaving}
			label={display.label}
			onUndo={onUndo}
		/>
	);
};
